import React from 'react';
import PluginHeader from '../PluginHeader';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";

const MyLogoPlugin = () => {
    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>My logo - Aronium</title>
                    <meta name="description" content="Display your company logo on Aronum's login screen" />
                </Helmet>
            </HelmetProvider>

            <section className="py-8 max-w-screen-xl px-4 mx-auto">
                <div className="max-w-screen-xl mx-auto text-center">
                    <h1 className="text-4xl mb-4">
                        My logo
                    </h1>
                    <p>
                        Display your company logo on login screen
                    </p>

                    {/* Call to Action Buttons */}
                    <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
                        {/* Download for Aronium Lite Button */}
                        <a
                            href={`${BASE_URL}/plugins/my-logo/latest/Aronium.Pos.Logo.Lite.exe`}
                            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-500 transition duration-300 focus:ring-4 focus:ring-pink-200 focus:outline-none transform hover:no-underline"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>
                            Download for Aronium Lite
                        </a>

                        {/* Download for Aronium Pro Button */}
                        <a
                            href={`${BASE_URL}/plugins/my-logo/latest/Aronium.Pos.Logo.Pro.exe`}
                            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-white bg-gradient-to-r from-[#7c8693] to-[#98a3b0] hover:from-[#98a3b0] hover:to-[#7c8693] transition duration-300 focus:ring-4 focus:ring-blue-200 focus:outline-none hover:no-underline"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>
                            Download for Aronium Pro
                        </a>
                    </div>
                </div>
            </section>

            {/* Overview Section */}
            <div className="flex flex-col text-center mt-10">
                <div className="grid place-content-center justify-items-center px-4">
                    <img src="/images/logo-login-screen.png" className='border-t-8 border-l border-r rounded-t-xl border-solid border-[#ff9600] xl:max-w-6xl' alt="Show your company logo on login screen" />
                </div>
            </div>
            <div className="bg-gradient-to-b from-slate-400 to-white py-10">
                <PluginHeader price="1" cssStyle='bg-white rounded-xl shadow-xl' monthlySubscriptionAvailable={false} note="* Annual subscription only" />
            </div>

            <section className='pt-12'>
                <div className="max-w-7xl mx-auto text-center px-8 lg:px-12">
                    <h2 className="text-5xl leading-tight mb-8">
                        Why use <span className='font-semibold'>My logo</span> plugin?
                        </h2>
                    <p className="text-lg max-w-5xl mx-auto">
                        The <span className='font-semibold'>My logo</span> plugin enables you to personalize your system by displaying your company logo on the login screen, thereby enhancing brand recognition and imparting a professional appearance to your point-of-sale interface.
                    </p>
                </div>
            </section>

            {/* Key Features Section */}
            <div className="py-24">
                <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                    <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
                        <h3 className="text-2xl font-semibold text-gray-800">Easy logo integration</h3>
                        <p className="mt-4 text-gray-600">
                            Upload your company logo directly through the Aronium management section.
                        </p>
                    </div>
                    <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
                        <h3 className="text-2xl font-semibold text-gray-800">Brand awareness</h3>
                        <p className="mt-4 text-gray-600">
                            Raise  brand awareness by helping customers memorize your logo and your company visuals.
                        </p>
                    </div>
                </div>
            </div>

            {/* Call to Action Section */}
            <div className="bg-slate-800 py-24 text-center text-white">
                <div className="mx-auto px-4">
                    <h2 className="text-4xl xl:text-6xl mb-6">Ready to elevate your branding?</h2>
                    <p className="text-xl mb-10">
                        Start using <span className='font-semibold'>My logo</span> plugin to effectively manage your brand identity.
                    </p>
                    <a
                        href="https://help.aronium.com/hc/en-us/articles/4410556069010-Adding-your-logo-to-the-login-screen"
                        className="button-rounded-white px-12 py-4">
                        Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                        </svg>
                    </a>
                </div>
            </div>
        </>
    );
};

export default MyLogoPlugin;
